import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, Download, Eye, FileText, ImageIcon, Loader2, Pencil, Plus, Search, Trash2, UploadCloud, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/LanguageContext';
import { useApiClient } from '../lib/apiClient';

type DocumentFieldType = 'text' | 'textarea' | 'number' | 'date';

type DocumentTypeField = {
  id: string;
  label: string;
  type: DocumentFieldType;
  required: boolean;
};

type DocumentTypeRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  fseDocumentTypeCode: string | null;
  isActive: boolean;
  fields: DocumentTypeField[];
};

type PatientDocument = {
  id: string;
  patientId: string;
  fileName: string;
  title: string;
  description: string | null;
  mimeType: string;
  storageKey: string | null;
  url: string;
  category: string | null;
  sizeBytes: number | null;
  reviewStatus: 'RECEIVED' | 'UNDER_REVIEW' | 'ACCEPTED' | 'REJECTED';
  reviewedAt: string | null;
  rejectionReason: string | null;
  metadata: Record<string, string | number | boolean | null> | null;
  folderId: string | null;
  folder: { id: string; name: string; portalVisible: boolean } | null;
  portalVisible: boolean;
  documentType: {
    id: string;
    code: string;
    name: string;
    fseDocumentTypeCode: string | null;
    fields: DocumentTypeField[];
  } | null;
  isLabResult: boolean;
  createdAt: string;
  uploadedBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
};

type PatientDocumentFolder = {
  id: string;
  name: string;
  portalVisible: boolean;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
};

type DocumentPreviewPayload = {
  previewUrl: string;
  expiresInSeconds: number;
  fileName: string;
  mimeType: string;
};

type UploadDraft = {
  file: File | null;
  title: string;
  description: string;
  documentTypeId: string;
  folderId: string;
  portalVisible: boolean;
  metadata: Record<string, string>;
};

function normalizeDocumentFieldType(value: unknown): DocumentFieldType {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'textarea' || normalized === 'number' || normalized === 'date') return normalized;
  return 'text';
}

function isInlinePreviewable(mimeType: string, fileName: string) {
  const mime = String(mimeType || '').toLowerCase();
  const lower = String(fileName || '').toLowerCase();
  if (mime.startsWith('image/')) return true;
  if (mime === 'application/pdf') return true;
  if (mime.startsWith('text/')) return true;
  if (mime.includes('json')) return true;
  return ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.gif', '.txt', '.json', '.csv'].some((ext) => lower.endsWith(ext));
}

function formatSize(bytes: number | null) {
  if (!bytes || bytes < 0) return '-';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx += 1;
  }
  return `${value.toFixed(unitIdx === 0 ? 0 : 1)} ${units[unitIdx]}`;
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function reviewBadgeClass(status: string) {
  const map: Record<string, string> = {
    RECEIVED: 'bg-slate-100 text-slate-700',
    UNDER_REVIEW: 'bg-amber-100 text-amber-800',
    ACCEPTED: 'bg-emerald-100 text-emerald-700',
    REJECTED: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}

function defaultUploadDraft(): UploadDraft {
  return {
    file: null,
    title: '',
    description: '',
    documentTypeId: '',
    folderId: '',
    portalVisible: false,
    metadata: {},
  };
}

function normalizeDateInputValue(value: string | number | boolean | null | undefined) {
  if (value === null || value === undefined || value === '') return '';
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) return asString;
  const parsed = new Date(asString);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
}

function createEditDraft(document: PatientDocument): UploadDraft {
  const metadata: Record<string, string> = {};
  const fieldMap = new Map((document.documentType?.fields || []).map((field) => [field.id, field]));
  for (const [key, value] of Object.entries(document.metadata || {})) {
    const field = fieldMap.get(key);
    if (normalizeDocumentFieldType(field?.type) === 'date') {
      metadata[key] = normalizeDateInputValue(value);
      continue;
    }
    metadata[key] = value === null || value === undefined ? '' : String(value);
  }
  return {
    file: null,
    title: document.title,
    description: document.description || '',
    documentTypeId: document.documentType?.id || '',
    folderId: document.folderId || '',
    portalVisible: document.portalVisible,
    metadata,
  };
}

function detectCategory(type: DocumentTypeRow | null) {
  return type?.code || 'patient-document';
}

function coerceMetadata(fields: DocumentTypeField[], source: Record<string, string>) {
  const result: Record<string, string | number | boolean | null> = {};
  for (const field of fields) {
    const rawValue = source[field.id];
    if (!rawValue?.trim()) continue;
    const fieldType = normalizeDocumentFieldType(field.type);
    if (fieldType === 'number') {
      result[field.id] = Number(rawValue);
      continue;
    }
    result[field.id] = rawValue.trim();
  }
  return result;
}

function formatMetadataValue(field: DocumentTypeField, value: string | number | boolean | null | undefined) {
  if (value === null || value === undefined || value === '') return null;
  if (normalizeDocumentFieldType(field.type) === 'date') {
    return new Date(String(value)).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
  return String(value);
}

export function PatientDocumentsModule({ patientId }: { patientId: string }) {
  const navigate = useNavigate();
  const { fetchJson, fetchApi } = useApiClient();
  const { tr } = useI18n();

  const [documents, setDocuments] = useState<PatientDocument[]>([]);
  const [folders, setFolders] = useState<PatientDocumentFolder[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeLoading, setTypeLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyDocumentId, setBusyDocumentId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [previewDocument, setPreviewDocument] = useState<PatientDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [libraryView, setLibraryView] = useState<'all' | 'lab'>('all');
  const [selectedFolderId, setSelectedFolderId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDraft, setUploadDraft] = useState<UploadDraft>(defaultUploadDraft());
  const [editingDocument, setEditingDocument] = useState<PatientDocument | null>(null);
  const [editDraft, setEditDraft] = useState<UploadDraft>(defaultUploadDraft());

  const inlinePreview = useMemo(() => {
    if (!previewDocument) return false;
    return isInlinePreviewable(previewDocument.mimeType, previewDocument.fileName);
  }, [previewDocument]);

  const selectedDocumentType = useMemo(
    () => documentTypes.find((item) => item.id === uploadDraft.documentTypeId) ?? null,
    [documentTypes, uploadDraft.documentTypeId],
  );
  const selectedEditDocumentType = useMemo(
    () => documentTypes.find((item) => item.id === editDraft.documentTypeId) ?? null,
    [documentTypes, editDraft.documentTypeId],
  );

  const labDocumentCount = useMemo(() => documents.filter((doc) => doc.isLabResult).length, [documents]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (libraryView === 'lab') params.set('kind', 'lab');
      if (selectedFolderId) params.set('folderId', selectedFolderId);
      if (searchTerm.trim()) params.set('q', searchTerm.trim());
      const suffix = params.toString() ? `?${params.toString()}` : '';
      const data = await fetchJson<PatientDocument[]>(`/patients/${patientId}/documents${suffix}`);
      setDocuments(data);
    } catch (err: any) {
      setError(err.message || tr('Unable to load patient documents.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, libraryView, patientId, searchTerm, selectedFolderId, tr]);

  const loadFolders = useCallback(async () => {
    try {
      const data = await fetchJson<PatientDocumentFolder[]>(`/patients/${patientId}/document-folders`);
      setFolders(data);
    } catch (err: any) {
      setError(err.message || tr('Unable to load document folders.'));
    }
  }, [fetchJson, patientId, tr]);

  const loadDocumentTypes = useCallback(async () => {
    setTypeLoading(true);
    try {
      const data = await fetchJson<DocumentTypeRow[]>('/document-types');
      setDocumentTypes((data || []).filter((item) => item.isActive));
    } catch (err: any) {
      setError(err.message || tr('Unable to load document types.'));
    } finally {
      setTypeLoading(false);
    }
  }, [fetchJson, tr]);

  useEffect(() => {
    loadDocuments();
    loadDocumentTypes();
    loadFolders();
  }, [loadDocuments, loadDocumentTypes, loadFolders]);

  async function requestPreview(document: PatientDocument) {
    setBusyDocumentId(document.id);
    setError(null);
    setBanner(null);
    try {
      const payload = await fetchJson<DocumentPreviewPayload>(`/patients/${patientId}/documents/${document.id}/preview`);
      if (isInlinePreviewable(document.mimeType, document.fileName)) {
        setPreviewDocument(document);
        setPreviewUrl(payload.previewUrl);
      } else {
        window.open(payload.previewUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err: any) {
      setError(err.message || tr('Failed to open document preview.'));
    } finally {
      setBusyDocumentId(null);
    }
  }

  async function downloadDocument(document: PatientDocument) {
    setBusyDocumentId(document.id);
    setError(null);
    try {
      const payload = await fetchJson<DocumentPreviewPayload>(`/patients/${patientId}/documents/${document.id}/download`);
      const linkEl = window.document.createElement('a');
      linkEl.href = payload.previewUrl;
      linkEl.download = payload.fileName || document.fileName;
      linkEl.target = '_blank';
      linkEl.rel = 'noopener noreferrer';
      linkEl.click();
      setBanner(tr('Document download started.'));
    } catch (err: any) {
      setError(err.message || tr('Unable to fetch document download URL.'));
    } finally {
      setBusyDocumentId(null);
    }
  }

  async function renameDocument(document: PatientDocument) {
    const nextTitle = window.prompt(tr('Rename document'), document.title);
    if (!nextTitle || !nextTitle.trim() || nextTitle.trim() === document.title) return;
    setBusyDocumentId(document.id);
    setError(null);
    setBanner(null);
    try {
      await fetchJson<PatientDocument>(`/patients/${patientId}/documents/${document.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: nextTitle.trim() }),
      });
      await loadDocuments();
      setBanner(tr('Document renamed.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to update document.'));
    } finally {
      setBusyDocumentId(null);
    }
  }

  async function createFolder() {
    const name = window.prompt(tr('New folder name'));
    if (!name?.trim()) return;
    try {
      await fetchJson<PatientDocumentFolder>(`/patients/${patientId}/document-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      await loadFolders();
      setBanner(tr('Folder created.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to create folder.'));
    }
  }

  async function renameFolder(folder: PatientDocumentFolder) {
    const name = window.prompt(tr('Rename folder'), folder.name);
    if (!name?.trim() || name.trim() === folder.name) return;
    try {
      await fetchJson<PatientDocumentFolder>(`/patients/${patientId}/document-folders/${folder.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() }),
      });
      await Promise.all([loadFolders(), loadDocuments()]);
      setBanner(tr('Folder renamed.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to rename folder.'));
    }
  }

  async function deleteFolder(folder: PatientDocumentFolder) {
    if (!window.confirm(`${tr('Delete folder')} "${folder.name}"? ${tr('Documents will move to Unfiled.')}`)) return;
    try {
      const res = await fetchApi(`/patients/${patientId}/document-folders/${folder.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      if (selectedFolderId === folder.id) setSelectedFolderId('');
      await Promise.all([loadFolders(), loadDocuments()]);
      setBanner(tr('Folder deleted.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to delete folder.'));
    }
  }

  async function toggleFolderPortal(folder: PatientDocumentFolder) {
    try {
      await fetchJson<PatientDocumentFolder>(`/patients/${patientId}/document-folders/${folder.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ portalVisible: !folder.portalVisible }),
      });
      await loadFolders();
      setBanner(tr('Folder portal visibility updated.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to update folder.'));
    }
  }

  async function moveDocument(document: PatientDocument, folderId: string) {
    setBusyDocumentId(document.id);
    setError(null);
    try {
      await fetchJson<PatientDocument>(`/patients/${patientId}/documents/${document.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderId: folderId || null }),
      });
      await Promise.all([loadFolders(), loadDocuments()]);
      setBanner(tr('Document moved.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to move document.'));
    } finally {
      setBusyDocumentId(null);
    }
  }

  async function toggleDocumentPortal(document: PatientDocument) {
    setBusyDocumentId(document.id);
    setError(null);
    try {
      await fetchJson<PatientDocument>(`/patients/${patientId}/documents/${document.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ portalVisible: !document.portalVisible }),
      });
      await loadDocuments();
      setBanner(tr('Portal document access updated.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to update document.'));
    } finally {
      setBusyDocumentId(null);
    }
  }

  async function deleteDocument(document: PatientDocument) {
    if (!window.confirm(`${tr('Delete')} "${document.title}"?`)) return;
    setBusyDocumentId(document.id);
    setError(null);
    setBanner(null);
    try {
      const res = await fetchApi(`/patients/${patientId}/documents/${document.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setDocuments((prev) => prev.filter((item) => item.id !== document.id));
      if (previewDocument?.id === document.id) {
        setPreviewDocument(null);
        setPreviewUrl('');
      }
      setBanner(tr('Document deleted.'));
    } catch (err: any) {
      setError(err.message || tr('Failed to delete document.'));
    } finally {
      setBusyDocumentId(null);
    }
  }

  function openEditDialog(document: PatientDocument) {
    setEditingDocument(document);
    setEditDraft(createEditDraft(document));
    setError(null);
    setBanner(null);
  }

  async function submitUpload() {
    if (!uploadDraft.file) {
      setError(tr('A file is required.'));
      return;
    }
    if (!uploadDraft.title.trim()) {
      setError(tr('Title is required.'));
      return;
    }
    if (!uploadDraft.documentTypeId) {
      setError(tr('Document type is required.'));
      return;
    }
    if (!selectedDocumentType) {
      setError(tr('Selected document type is invalid.'));
      return;
    }
    for (const field of selectedDocumentType.fields) {
      if (field.required && !uploadDraft.metadata[field.id]?.trim()) {
        setError(`${field.label} is required.`);
        return;
      }
    }

    setUploading(true);
    setError(null);
    setBanner(null);

    try {
      const file = uploadDraft.file;
      const presign = await fetchJson<{ key: string; uploadUrl: string; url: string }>(`/storage/presign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type || 'application/octet-stream',
          kind: 'patient-document',
          patientId,
        }),
      });

      const uploadRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed for "${file.name}" (${uploadRes.status})`);
      }

      await fetchJson<PatientDocument>(`/patients/${patientId}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: uploadDraft.title.trim(),
          description: uploadDraft.description.trim() || undefined,
          documentTypeId: selectedDocumentType.id,
          folderId: uploadDraft.folderId || null,
          portalVisible: uploadDraft.portalVisible,
          metadata: coerceMetadata(selectedDocumentType.fields, uploadDraft.metadata),
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          storageKey: presign.key,
          url: presign.url,
          sizeBytes: file.size,
          category: detectCategory(selectedDocumentType),
        }),
      });

      await loadDocuments();
      setBanner(tr('Document uploaded successfully.'));
      setUploadDialogOpen(false);
      setUploadDraft(defaultUploadDraft());
    } catch (err: any) {
      setError(err.message || tr('Document upload failed.'));
    } finally {
      setUploading(false);
    }
  }

  async function submitEdit() {
    if (!editingDocument) return;
    if (!editDraft.title.trim()) {
      setError(tr('Title is required.'));
      return;
    }
    if (!editDraft.documentTypeId) {
      setError(tr('Document type is required.'));
      return;
    }
    if (!selectedEditDocumentType) {
      setError(tr('Selected document type is invalid.'));
      return;
    }
    for (const field of selectedEditDocumentType.fields) {
      if (field.required && !editDraft.metadata[field.id]?.trim()) {
        setError(`${field.label} is required.`);
        return;
      }
    }

    setUploading(true);
    setError(null);
    setBanner(null);
    try {
      await fetchJson<PatientDocument>(`/patients/${patientId}/documents/${editingDocument.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editDraft.title.trim(),
          description: editDraft.description.trim() || undefined,
          documentTypeId: selectedEditDocumentType.id,
          folderId: editDraft.folderId || null,
          portalVisible: editDraft.portalVisible,
          metadata: coerceMetadata(selectedEditDocumentType.fields, editDraft.metadata),
          category: detectCategory(selectedEditDocumentType),
          reviewStatus: editingDocument.reviewStatus,
          rejectionReason: editingDocument.rejectionReason || undefined,
        }),
      });
      await loadDocuments();
      setBanner(tr('Document updated successfully.'));
      setEditingDocument(null);
      setEditDraft(defaultUploadDraft());
    } catch (err: any) {
      setError(err.message || tr('Failed to update document.'));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Documents')}</p>
            <h3 className="text-base font-semibold text-slate-900">{tr('Document Library')}</h3>
            <p className="text-xs text-slate-500">{documents.length} {tr('documents')} · {labDocumentCount} {tr('lab result documents')}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setUploadDraft(defaultUploadDraft());
              setUploadDialogOpen(true);
              setError(null);
              setBanner(null);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-lime-400 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-lime-300"
          >
            <Plus size={16} />
            {tr('Add Document')}
          </button>
        </div>

        {banner ? <div className="mx-4 mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{banner}</div> : null}
        {error ? <div className="mx-4 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}

        <div className="flex flex-col gap-3 border-b border-slate-100 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="inline-flex w-fit rounded-xl border border-slate-200 bg-white p-1 text-sm">
            {[
              ['all', 'All documents'],
              ['lab', 'Lab result documents'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setLibraryView(value as 'all' | 'lab')}
                className={`rounded-lg px-3 py-1.5 font-medium ${libraryView === value ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                {tr(label)}
              </button>
            ))}
          </div>
          <label className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm md:w-80">
            <Search size={15} className="text-slate-400" />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent outline-none"
              placeholder={tr('Search documents')}
            />
          </label>
        </div>

        <div className="border-b border-slate-100 px-4 py-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">{tr('Document Folders')}</p>
            <button type="button" onClick={createFolder} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
              {tr('Create folder')}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedFolderId('')}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${!selectedFolderId ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600'}`}
            >
              {tr('All folders')}
            </button>
            <button
              type="button"
              onClick={() => setSelectedFolderId('unfiled')}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${selectedFolderId === 'unfiled' ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 text-slate-600'}`}
            >
              {tr('Unfiled')}
            </button>
            {folders.map((folder) => (
              <span key={folder.id} className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs ${selectedFolderId === folder.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
                <button type="button" onClick={() => setSelectedFolderId(folder.id)} className="font-medium">
                  {folder.name} ({folder.documentCount})
                </button>
                {folder.portalVisible ? <span title={tr('Portal Documents')}>P</span> : null}
                <button type="button" onClick={() => renameFolder(folder)} className="px-1 underline" title={tr('Rename Folder')}>{tr('Rename')}</button>
                <button type="button" onClick={() => toggleFolderPortal(folder)} className="px-1 underline" title={tr('Portal Documents')}>{folder.portalVisible ? tr('Hide') : tr('Portal')}</button>
                <button type="button" onClick={() => deleteFolder(folder)} className="px-1 text-rose-600 underline" title={tr('Delete Folder')}>{tr('Delete')}</button>
              </span>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">{tr('Document')}</th>
                <th className="px-4 py-3 font-medium">{tr('Type')}</th>
                <th className="px-4 py-3 font-medium">{tr('Folder')}</th>
                <th className="px-4 py-3 font-medium">{tr('Portal')}</th>
                <th className="px-4 py-3 font-medium">{tr('Review')}</th>
                <th className="px-4 py-3 font-medium">{tr('Uploaded')}</th>
                <th className="px-4 py-3 font-medium">{tr('Details')}</th>
                <th className="px-4 py-3 text-right font-medium">{tr('Actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <Loader2 size={15} className="animate-spin" />
                      {tr('Loading documents...')}
                    </span>
                  </td>
                </tr>
              ) : !documents.length ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-slate-500">
                    {tr('No documents uploaded for this patient.')}
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <tr key={doc.id} className="align-top">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => requestPreview(doc)} className="flex items-start gap-2 text-left">
                        {doc.mimeType.startsWith('image/') ? <ImageIcon size={16} className="mt-0.5 text-sky-600" /> : <FileText size={16} className="mt-0.5 text-slate-500" />}
                        <span>
                          <span className="block font-medium text-slate-900 hover:text-sky-700">{doc.title}</span>
                          <span className="block text-xs text-slate-500">{doc.fileName}</span>
                          {doc.isLabResult ? <span className="mt-1 inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700">{tr('Lab result')}</span> : null}
                        </span>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{doc.documentType?.name || doc.category || '-'}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <select
                        value={doc.folderId || ''}
                        onChange={(event) => void moveDocument(doc, event.target.value)}
                        disabled={busyDocumentId === doc.id}
                        className="w-36 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs"
                      >
                        <option value="">{tr('Unfiled')}</option>
                        {folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <label className="inline-flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={doc.portalVisible}
                          onChange={() => void toggleDocumentPortal(doc)}
                          disabled={busyDocumentId === doc.id}
                          className="accent-slate-900"
                        />
                        {doc.portalVisible ? tr('Enabled') : tr('Hidden')}
                      </label>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${reviewBadgeClass(doc.reviewStatus)}`}>{tr(doc.reviewStatus)}</span>
                      </div>
                      <div className="mt-1 text-xs text-slate-500">{tr('Reviewed')} {formatDate(doc.reviewedAt)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      <div>{formatDate(doc.createdAt)}</div>
                      <div className="text-xs text-slate-500">
                        {doc.uploadedBy.firstName} {doc.uploadedBy.lastName}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {doc.description ? <div className="mb-1 line-clamp-2">{doc.description}</div> : null}
                      <div className="text-xs text-slate-500">{formatSize(doc.sizeBytes)}</div>
                      {doc.rejectionReason ? <div className="mt-1 text-xs text-rose-600">{doc.rejectionReason}</div> : null}
                      {doc.documentType?.fields?.length ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {doc.documentType.fields.map((field) => {
                            const value = formatMetadataValue(field, doc.metadata?.[field.id]);
                            if (!value) return null;
                            return (
                              <span key={field.id} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
                                {field.label}: {value}
                              </span>
                            );
                          })}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEditDialog(doc)}
                          disabled={busyDocumentId === doc.id}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                          title={tr('Edit')}
                          aria-label={tr('Edit')}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => renameDocument(doc)}
                          disabled={busyDocumentId === doc.id}
                          className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                          title={tr('Rename Document')}
                          aria-label={tr('Rename Document')}
                        >
                          {tr('Rename')}
                        </button>
                        <button
                          type="button"
                          onClick={() => requestPreview(doc)}
                          disabled={busyDocumentId === doc.id}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                          title={tr('Preview')}
                          aria-label={tr('Preview')}
                        >
                          {busyDocumentId === doc.id ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadDocument(doc)}
                          disabled={busyDocumentId === doc.id}
                          className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                          title={tr('Download')}
                          aria-label={tr('Download')}
                        >
                          <Download size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const params = new URLSearchParams({
                              sourceType: 'PATIENT_DOCUMENT',
                              sourceId: doc.id,
                            });
                            if (doc.documentType?.fseDocumentTypeCode) {
                              params.set('documentTypeCode', doc.documentType.fseDocumentTypeCode);
                            }
                            navigate(`/fse?${params.toString()}`);
                          }}
                          disabled={busyDocumentId === doc.id}
                          className="rounded-lg border border-sky-200 p-2 text-sky-700 hover:bg-sky-50 disabled:opacity-60"
                          title={tr('Open FSE Publication')}
                          aria-label={tr('Open FSE Publication')}
                        >
                          <ArrowUpRight size={15} />
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteDocument(doc)}
                          disabled={busyDocumentId === doc.id}
                          className="rounded-lg border border-rose-200 p-2 text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                          title={tr('Delete')}
                          aria-label={tr('Delete')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {uploadDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => !uploading && setUploadDialogOpen(false)}>
          <div className="mt-10 w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">{tr('Documents')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{tr('Upload New Document')}</h2>
                <p className="mt-1 text-sm text-slate-500">{tr('Give the document a clear title, choose its type, and complete any required metadata before uploading.')}</p>
              </div>
              <button
                type="button"
                onClick={() => !uploading && setUploadDialogOpen(false)}
                className="rounded-xl border border-slate-300 bg-white p-2 text-slate-700 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <label className="space-y-1 text-sm text-slate-700 md:col-span-2">
                <span>{tr('File')}</span>
                <label className="flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 hover:border-slate-400">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{uploadDraft.file ? uploadDraft.file.name : tr('Select a file to upload')}</div>
                    <div className="text-xs text-slate-500">{uploadDraft.file ? formatSize(uploadDraft.file.size) : tr('Supported files include PDF, images, text, and common chart attachments.')}</div>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700">
                    <UploadCloud size={16} />
                    {tr('Browse')}
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      event.currentTarget.value = '';
                      setUploadDraft((prev) => ({
                        ...prev,
                        file,
                        title: prev.title || (file ? file.name.replace(/\.[^.]+$/, '') : ''),
                      }));
                    }}
                  />
                </label>
              </label>

              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Title')}</span>
                <input
                  value={uploadDraft.title}
                  onChange={(event) => setUploadDraft((prev) => ({ ...prev, title: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                  placeholder={tr('Example: Lab report, referral letter, or consent form')}
                />
              </label>

              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Document Type')}</span>
                <select
                  value={uploadDraft.documentTypeId}
                  onChange={(event) =>
                    setUploadDraft((prev) => ({
                      ...prev,
                      documentTypeId: event.target.value,
                      metadata: {},
                    }))
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                >
                  <option value="">{typeLoading ? tr('Loading document types...') : tr('Select a document type')}</option>
                  {documentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Folder')}</span>
                <select
                  value={uploadDraft.folderId}
                  onChange={(event) => setUploadDraft((prev) => ({ ...prev, folderId: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                >
                  <option value="">{tr('Unfiled')}</option>
                  {folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
                </select>
              </label>

              <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={uploadDraft.portalVisible}
                  onChange={(event) => setUploadDraft((prev) => ({ ...prev, portalVisible: event.target.checked }))}
                  className="accent-slate-900"
                />
                {tr('Enable in client portal')}
              </label>

              <label className="space-y-1 text-sm text-slate-700 md:col-span-2">
                <span>{tr('Description')}</span>
                <textarea
                  value={uploadDraft.description}
                  onChange={(event) => setUploadDraft((prev) => ({ ...prev, description: event.target.value }))}
                  className="min-h-[88px] w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                  placeholder={tr('Optional note for clinicians reviewing this document.')}
                />
              </label>
            </div>

            {selectedDocumentType ? (
              <div className="mt-5 space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedDocumentType.name} Fields</h3>
                  {selectedDocumentType.description ? <p className="mt-1 text-sm text-slate-500">{selectedDocumentType.description}</p> : null}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {selectedDocumentType.fields.map((field) => (
                    <label key={field.id} className={`space-y-1 text-sm text-slate-700 ${normalizeDocumentFieldType(field.type) === 'textarea' ? 'md:col-span-2' : ''}`}>
                      <span>
                        {field.label}
                        {field.required ? ' *' : ''}
                      </span>
                      {normalizeDocumentFieldType(field.type) === 'textarea' ? (
                        <textarea
                          value={uploadDraft.metadata[field.id] || ''}
                          onChange={(event) =>
                            setUploadDraft((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, [field.id]: event.target.value },
                            }))
                          }
                          className="min-h-[88px] w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                        />
                      ) : (
                        <input
                          type={normalizeDocumentFieldType(field.type) === 'number' ? 'number' : normalizeDocumentFieldType(field.type) === 'date' ? 'date' : 'text'}
                          value={uploadDraft.metadata[field.id] || ''}
                          onChange={(event) =>
                            setUploadDraft((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, [field.id]: event.target.value },
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            {!typeLoading && !documentTypes.length ? (
              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {tr('No document types are configured yet. Create one in Settings before uploading typed documents.')}
              </div>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {tr('Cancel')}
              </button>
              <button
                type="button"
                onClick={submitUpload}
                disabled={uploading || typeLoading || !documentTypes.length}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <UploadCloud size={16} />}
                {uploading ? tr('Uploading...') : tr('Upload Document')}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingDocument && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => !uploading && setEditingDocument(null)}>
          <div className="mt-10 w-full max-w-3xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">{tr('Documents')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{tr('Edit Document')}</h2>
                <p className="mt-1 text-sm text-slate-500">{tr('Update the title, description, document type, and required metadata for this file.')}</p>
              </div>
              <button
                type="button"
                onClick={() => !uploading && setEditingDocument(null)}
                className="rounded-xl border border-slate-300 bg-white p-2 text-slate-700 hover:bg-slate-50"
              >
                <X size={16} />
              </button>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="space-y-1 text-sm text-slate-700 md:col-span-2">
                <span>{tr('File')}</span>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                  <div className="font-medium text-slate-900">{editingDocument.fileName}</div>
                  <div className="text-xs text-slate-500">{formatSize(editingDocument.sizeBytes)} • {editingDocument.mimeType}</div>
                </div>
              </div>

              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Title')}</span>
                <input
                  value={editDraft.title}
                  onChange={(event) => setEditDraft((prev) => ({ ...prev, title: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                />
              </label>

              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Document Type')}</span>
                <select
                  value={editDraft.documentTypeId}
                  onChange={(event) =>
                    setEditDraft((prev) => ({
                      ...prev,
                      documentTypeId: event.target.value,
                      metadata: {},
                    }))
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                >
                  <option value="">{typeLoading ? tr('Loading document types...') : tr('Select a document type')}</option>
                  {documentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Folder')}</span>
                <select
                  value={editDraft.folderId}
                  onChange={(event) => setEditDraft((prev) => ({ ...prev, folderId: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                >
                  <option value="">{tr('Unfiled')}</option>
                  {folders.map((folder) => <option key={folder.id} value={folder.id}>{folder.name}</option>)}
                </select>
              </label>

              <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={editDraft.portalVisible}
                  onChange={(event) => setEditDraft((prev) => ({ ...prev, portalVisible: event.target.checked }))}
                  className="accent-slate-900"
                />
                {tr('Enable in client portal')}
              </label>

              <label className="space-y-1 text-sm text-slate-700 md:col-span-2">
                <span>{tr('Description')}</span>
                <textarea
                  value={editDraft.description}
                  onChange={(event) => setEditDraft((prev) => ({ ...prev, description: event.target.value }))}
                  className="min-h-[88px] w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Review Status')}</span>
                <select
                  value={editingDocument.reviewStatus}
                  onChange={(event) =>
                    setEditingDocument((current) => (current ? { ...current, reviewStatus: event.target.value as PatientDocument['reviewStatus'] } : current))
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                >
                  <option value="RECEIVED">{tr('RECEIVED')}</option>
                  <option value="UNDER_REVIEW">{tr('UNDER_REVIEW')}</option>
                  <option value="ACCEPTED">{tr('ACCEPTED')}</option>
                  <option value="REJECTED">{tr('REJECTED')}</option>
                </select>
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span>{tr('Review Note')}</span>
                <input
                  value={editingDocument.rejectionReason || ''}
                  onChange={(event) =>
                    setEditingDocument((current) => (current ? { ...current, rejectionReason: event.target.value } : current))
                  }
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                  placeholder={tr('Optional explanation for rejection or follow-up')}
                />
              </label>
            </div>

            {selectedEditDocumentType ? (
              <div className="mt-5 space-y-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedEditDocumentType.name} Fields</h3>
                  {selectedEditDocumentType.description ? <p className="mt-1 text-sm text-slate-500">{selectedEditDocumentType.description}</p> : null}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  {selectedEditDocumentType.fields.map((field) => (
                    <label key={field.id} className={`space-y-1 text-sm text-slate-700 ${normalizeDocumentFieldType(field.type) === 'textarea' ? 'md:col-span-2' : ''}`}>
                      <span>
                        {field.label}
                        {field.required ? ' *' : ''}
                      </span>
                      {normalizeDocumentFieldType(field.type) === 'textarea' ? (
                        <textarea
                          value={editDraft.metadata[field.id] || ''}
                          onChange={(event) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, [field.id]: event.target.value },
                            }))
                          }
                          className="min-h-[88px] w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                        />
                      ) : (
                        <input
                          type={normalizeDocumentFieldType(field.type) === 'number' ? 'number' : normalizeDocumentFieldType(field.type) === 'date' ? 'date' : 'text'}
                          value={editDraft.metadata[field.id] || ''}
                          onChange={(event) =>
                            setEditDraft((prev) => ({
                              ...prev,
                              metadata: { ...prev.metadata, [field.id]: event.target.value },
                            }))
                          }
                          className="w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-slate-400"
                        />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditingDocument(null)}
                disabled={uploading}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {tr('Cancel')}
              </button>
              <button
                type="button"
                onClick={submitEdit}
                disabled={uploading || typeLoading || !documentTypes.length}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Pencil size={16} />}
                {uploading ? tr('Saving...') : tr('Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewDocument && previewUrl ? (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-5xl max-h-[92vh] rounded-xl shadow-soft border border-slate-200 overflow-hidden">
            <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">{previewDocument.title}</p>
                <p className="text-xs text-slate-500">{previewDocument.fileName}</p>
              </div>
              <button
                onClick={() => {
                  setPreviewDocument(null);
                  setPreviewUrl('');
                }}
                className="px-3 py-1.5 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
              >
                {tr('Close')}
              </button>
            </div>
            <div className="p-3 bg-slate-50">
              {inlinePreview && previewDocument.mimeType.startsWith('image/') ? (
                <div className="max-h-[78vh] overflow-auto flex justify-center">
                  <img src={previewUrl} alt={previewDocument.fileName} className="max-h-[76vh] w-auto rounded border border-slate-200 bg-white" />
                </div>
              ) : (
                <iframe src={previewUrl} title={previewDocument.fileName} className="w-full h-[78vh] border border-slate-200 rounded bg-white" />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
